import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AccountSettingsService } from '../account-settings.service';
import { SessionService } from '../session.service';
import { ToastService } from '../toast/toast.service';

const USERNAME_PATTERN = /^[A-Za-z0-9_@.-]{6,}$/;
const NUMBERS_ONLY_PATTERN = /^\d+$/;

@Component({
  selector: 'app-account-change-username',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule],
  templateUrl: './account-change-username.component.html',
  styleUrl: '../account-change/_account-change-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountChangeUsernameComponent {
  private readonly fb = inject(FormBuilder);
  private readonly accountSettingsService = inject(AccountSettingsService);
  private readonly dialogRef = inject(MatDialogRef<AccountChangeUsernameComponent, boolean>);
  private readonly toastService = inject(ToastService);
  private readonly session = inject(SessionService);

  protected readonly currentUser = this.session.currentUser;
  protected readonly isSubmitting = signal(false);
  protected readonly errorMessageKey = signal<string | null>(null);
  protected readonly form = this.fb.nonNullable.group({
    username: ['', [Validators.required, Validators.pattern(USERNAME_PATTERN), usernameNotNumbersOnlyValidator]]
  });

  protected submit(): void {
    if (this.isSubmitting()) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessageKey.set(null);

    this.accountSettingsService
      .changeUsername(this.form.controls.username.value)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.toastService.showSuccess('account.changeUsername.success');
          this.dialogRef.close(true);
        },
        error: () => this.errorMessageKey.set('account.changeUsername.failed')
      });
  }

  protected close(): void {
    this.dialogRef.close(false);
  }
}

const usernameNotNumbersOnlyValidator: ValidatorFn = control => (
  NUMBERS_ONLY_PATTERN.test(control.value as string) ? { numbersOnly: true } : null
);
